import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Box, TextField, Grid } from '@mui/material';
import ExpandableCard from './ExpandableCard';

// Recibe {"maquina":3,"pieza":"..."} escrito en el input (Enter) o por socket 'maquinaPieza'
// (MQTT "pieza" o POST /api/maquina/pieza). Solo se muestra, no se guarda.
const API_URL = process.env.REACT_APP_API_URL || 'http://31.97.100.31:5002/api';

const formatHora = (d) => d
    ? d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
    : '--:--:--';

const parsearMaquinaPieza = (texto) => {
    const datos = JSON.parse(texto);
    const maquina = Number(datos?.maquina);
    if (!Number.isFinite(maquina) || datos?.pieza === undefined || datos?.pieza === null) {
        throw new Error('Formato esperado: {"maquina":3,"pieza":"..."}');
    }
    return { maquina, pieza: String(datos.pieza), recibido: new Date().toISOString() };
};

const MaquinaPieza = ({ socket, theme }) => {
    const [dato, setDato] = useState(null);
    const [entrada, setEntrada] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        const handleMaquinaPieza = (data) => setDato(data);
        socket.on('maquinaPieza', handleMaquinaPieza);

        // Último valor que tenga el backend en memoria, para no arrancar vacío
        let cancelado = false;
        fetch(`${API_URL}/maquina/pieza`)
            .then(r => (r.ok ? r.json() : null))
            .then(ultimo => {
                if (!cancelado && ultimo) setDato(prev => prev ?? ultimo);
            })
            .catch(() => {});

        return () => {
            cancelado = true;
            socket.off('maquinaPieza', handleMaquinaPieza);
        };
    }, [socket]);

    const procesarEntrada = () => {
        if (!entrada.trim()) return;
        try {
            setDato(parsearMaquinaPieza(entrada.trim()));
            setEntrada('');
            setError('');
        } catch (err) {
            setError(err instanceof SyntaxError ? 'JSON inválido: {"maquina":3,"pieza":"..."}' : err.message);
        }
    };

    const inputSx = {
        '& .MuiInputBase-root': { backgroundColor: theme.cardBg2, color: theme.text, boxShadow: theme.shadowInset },
        '& .MuiInputBase-input': { color: theme.text, WebkitTextFillColor: theme.text },
        '& .MuiInputLabel-root': { color: theme.textSub },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.borderCard },
        '& .MuiFormHelperText-root': { color: theme.textMuted },
    };

    const panelSx = {
        border: `1px solid ${theme.borderCard}`,
        borderRadius: '6px',
        backgroundColor: theme.cardBg2,
        boxShadow: theme.shadowInset,
        p: 1,
        height: '100%',
    };

    return (
        <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={12}>
                <ExpandableCard>
                    <Card sx={{ backgroundColor: theme.cardBg, color: theme.text, boxShadow: theme.shadow, border: `1px solid ${theme.borderCard}`, borderRadius: '10px' }}>
                        <CardContent sx={{ p: { xs: 0.5, sm: 2 }, pb: { xs: 0.5, sm: 2 } }}>
                            <Typography variant="h7" component="div" sx={{ borderBottom: `1px solid ${theme.border}`, pb: 1, mb: 1.5, color: theme.text, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                <Box component="span" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, fontWeight: 'bold' }}>Máquina · Pieza</Box>
                                <Box component="span" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, color: theme.textSub }}>
                                    última {formatHora(dato?.recibido ? new Date(dato.recibido) : null)}
                                </Box>
                            </Typography>
                            <Grid container spacing={1} alignItems="stretch">
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        label="Dato de la máquina"
                                        value={entrada}
                                        onChange={(e) => { setEntrada(e.target.value); setError(''); }}
                                        onKeyDown={(e) => { if (e.key === 'Enter') procesarEntrada(); }}
                                        placeholder='{"maquina":3,"pieza":"..."}'
                                        size="small"
                                        fullWidth
                                        error={Boolean(error)}
                                        helperText={error || 'Pegue o reciba el JSON y presione Enter'}
                                        sx={inputSx}
                                    />
                                </Grid>
                                <Grid item xs={4} md={2}>
                                    <Box sx={panelSx}>
                                        <Typography sx={{ color: theme.textMuted, fontSize: 10 }}>Máquina</Typography>
                                        <Typography sx={{ color: theme.text, fontWeight: 'bold', fontSize: '1.1rem' }}>{dato?.maquina ?? '-'}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={8} md={4}>
                                    <Box sx={panelSx}>
                                        <Typography sx={{ color: theme.textMuted, fontSize: 10 }}>Pieza</Typography>
                                        <Typography sx={{ color: theme.text, fontWeight: 'bold', fontSize: '1.1rem', wordBreak: 'break-word' }}>{dato?.pieza ?? '-'}</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </ExpandableCard>
            </Grid>
        </Grid>
    );
};

export default MaquinaPieza;
